import dayjs from "dayjs";
import i18n, { t } from "i18next";
import { CustomDate, RecentSearch } from "../../types/common";
import { DocInfo, FolderInfoDTO } from "../../types/files";
import { LoginDto } from "../../types/auth";
import {
  getAdminInfoFromLocalStorage,
  getUserInfoFromLocalStorage,
  getWorkspaceName,
} from "./auth";
import { meetingDTO } from "../../types/meeting";
import { Member, Workgroup } from "../../types/workgroup";
import { DEMO_HOSTNAME } from "../..";
import { getAppNameByExtension } from "./msAppConfig";
const utilsCommon = {
  getLanguage: () => {
    return window.navigator.language === "ar"
      ? "ar"
      : window.navigator.language === "ko" ||
        window.navigator.language === "ko-KR" ||
        window.navigator.language === "ko-kr"
      ? "ko"
      : window.navigator.language === "en" ||
        window.navigator.language === "en-US" ||
        window.navigator.language === "en-us"
      ? "en"
      : window.navigator.language === "ko-KR" ||
        window.navigator.language === "ko-kr"
      ? "ko"
      : "en";
  },
  renderViewTooltip: (contentName: string): string => {
    const fileType = contentName
      ? contentName.substring(contentName.lastIndexOf(".") + 1)
      : "";
    if (!window.eco.isCspp) {
      return t("file.action.preview");
    }
    const appName = getAppNameByExtension(fileType);
    if (appName) {
      return t("file.onlineOfficeView", { office: "Microsoft " + appName });
    } else if (["docx", "doc"].includes(fileType)) {
      return t("file.onlineOfficeView", { office: "Microsoft Word" });
    } else if (["pptx", "ppt"].includes(fileType)) {
      return t("file.onlineOfficeView", { office: "Microsoft PowerPoint" });
    } else if (["xlsx", "xls"].includes(fileType)) {
      return t("file.onlineOfficeView", { office: "Microsoft Excel" });
    } else {
      return t("file.action.preview");
    }
  },
  renderEditTooltip: (contentName: string): string => {
    const fileType = contentName
      ? contentName.substring(contentName.lastIndexOf(".") + 1)
      : "";
    const appName = window.eco.isCspp ? getAppNameByExtension(fileType) : null;

    return appName
      ? t("file.onlineOfficeCheckout", { office: "Microsoft " + appName })
      : t("file.webCheckout");
  },
  passwordValidation: (password: string, min: number, max: number): boolean => {
    const regex = new RegExp(
      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&+=*_-]).{${min},${max}}$`
    );
    return regex.test(password);
  },
  emailValidation: (email: string): boolean => {
    const regex =
      /^[0-9a-zA-Z+][-_.0-9a-zA-Z+]*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])+\.[a-zA-Z0-9-.]+$/;

    return regex.test(email);
  },
  phoneNumberValidation: (phone: string): boolean => {
    const regex = /^[0-9-]*$/;
    return regex.test(phone);
  },
  renderShortDateFormat: (
    date?: number | string,
    format?: string,
    lang?: string
  ): string => {
    require("dayjs/locale/ar");
    require("dayjs/locale/en");

    if (format && format.includes("월")) {
      dayjs.locale("ko");
    } else {
      dayjs.locale(lang ?? "en");
    }
    const returnFormat =
      format ?? (i18n.language.includes("ar") ? "DD/MM/YYYY" : "YYYY-MM-DD");
    return dayjs(date).format(returnFormat);
  },
  renderDateFormat: (
    date?: number | string,
    format?: string,
    lang?: string,
    isUTC?: boolean
  ): string => {
    require("dayjs/locale/ar");
    require("dayjs/locale/en");

    if (format && format.includes("월")) {
      dayjs.locale("ko");
    } else {
      dayjs.locale(lang ?? "en");
    }
    const returnFormat =
      format ??
      (i18n.language.includes("ar")
        ? "DD/MM/YYYY hh:mm A"
        : "YYYY-MM-DD, hh:mm A");
    return isUTC
      ? dayjs(date).tz("UTC").format(returnFormat)
      : dayjs(date).format(returnFormat);
  },
  userIdValidation: (username: string) => {
    const regex = /^[-_.@0-9A-Za-z]+$/;
    return regex.test(username);
  },
  maxLengthValidation: (text: string, maxLength: number) => {
    if (text.length > maxLength) {
      return false;
    } else {
      return true;
    }
  },
  sortMembersToAvatarGroup: (
    members?: Member[] | null,
    managerId?: string
  ): {
    imgSrc?: string | null;
    uniqueCode: string;
    name?: string | null;
    shape?: "user" | "team";
  }[] => {
    return members
      ? members
          ?.slice()
          ?.sort((a, b) =>
            a.userId === managerId
              ? -1
              : b.userId === managerId
              ? 1
              : a.userType - b.userType
          )
          ?.map((item) => {
            return {
              uniqueCode: utilsCommon.getUserColor(
                item.userType,
                item.status,
                item?.userName
              ),
              name: item.userName || item.userId,
              icon: item.userName ? undefined : "user",
            };
          })
      : [];
  },
  checkSupportPolicy: (
    checkBy: "Mobile" | "Mac" | "Web",
    isAdmin?: boolean
  ): string => {
    var temp = {} as any;
    var ecoUser =
      isAdmin && window.eco.serviceType !== "OCI"
        ? window.localStorage.getItem("fasoo-eco-admin")
        : window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");

    var systemLisenceStats = false;
    var commonConfig = false;
    var webOfficeType = "";
    switch (checkBy) {
      case "Mobile":
        systemLisenceStats = temp?.ecoSystemLicenseStats?.allowMobile;
        commonConfig = temp?.ecoCommonConfig?.allowMobile;
        break;
      case "Mac":
        systemLisenceStats = temp?.ecoSystemLicenseStats?.allowMacOS;
        commonConfig = temp?.ecoCommonConfig?.allowMacOS;
        break;
      case "Web":
        systemLisenceStats = temp?.ecoSystemLicenseStats?.allowWebView;
        webOfficeType = temp?.ecoCommonConfig?.webOfficeAllowUserType;
        if (!systemLisenceStats) {
          return "NOT_SUPPORTED";
        } else {
          if (webOfficeType === "DENY") {
            return "ADMIN_NOT_ALLOW";
          } else {
            return "SUPPORT_ACCEPTED";
          }
        }
    }
    if (systemLisenceStats === false) {
      if (window.eco.serviceType === "CLOUD") {
        return "NOT_SUPPORTED";
      } else {
        return "ADMIN_NOT_ALLOW";
      }
    } else {
      if (commonConfig) {
        return "SUPPORT_ACCEPTED";
      } else return "ADMIN_NOT_ALLOW";
    }
  },
  getNewRandomMeetingPW: () => {
    let password = "";
    const possible =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    Array.from(Array(6)).forEach(() => {
      password += possible.charAt(Math.floor(Math.random() * possible.length));
    });
    return password;
  },
  getFormatGMTOffset(offset: number): string {
    const sign = offset >= 0 ? "+" : "-";
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset);
    const minutes = Math.round((absOffset - hours) * 60);

    const hourStr = String(hours).padStart(2, "0");
    const minuteStr = String(minutes).padStart(2, "0");

    return `GMT${sign}${hourStr}:${minuteStr}`;
  },
  getTimeZoneOffset: (timeZone?: string): number => {
    if (!timeZone) return 0;
    const date = new Date();
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(date.toLocaleString("en-US", { timeZone }));

    const diff = (tzDate.getTime() - utcDate.getTime()) / 6e4 / 60;

    return diff;
  },
  getChatAvailibility: (): string => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");
    else return "noDisplay";
    var serviceConfig = temp.serviceConfig;
    return "" + serviceConfig.chatAvailibility;
  },
  checkMenuAvailable: (menu: string): boolean => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");
    else return false;
    var serviceConfig = temp.serviceConfig;
    const ecoSystemLicenseStats = temp.ecoSystemLicenseStats;
    if (ecoSystemLicenseStats?.featureAccessType === "LITE") {
      return false;
    } else if (menu === "zoom" && serviceConfig?.zoomAvailibility) {
      return serviceConfig?.zoomAvailibility;
    } else if (menu === "chat" && serviceConfig?.chatAvailibility) {
      return serviceConfig?.chatAvailibility;
    } else {
      return false;
    }
  },
  getDateTimeNum: (time: string | number): number => {
    return dayjs(time).valueOf();
  },
  getStartPeriod: (type: CustomDate["type"]): number => {
    const now = dayjs();
    switch (type) {
      case "1day":
        return utilsCommon.getDateTimeNum(
          now.subtract(1, "day").format("YYYY-MM-DD 00:00:00")
        );
      case "1week":
        return utilsCommon.getDateTimeNum(
          now.subtract(1, "week").format("YYYY-MM-DD 00:00:00")
        );
      case "1month":
        return utilsCommon.getDateTimeNum(
          now.subtract(1, "month").format("YYYY-MM-DD 00:00:00")
        );
      case "3months":
        return utilsCommon.getDateTimeNum(
          now.subtract(3, "month").format("YYYY-MM-DD 00:00:00")
        );
      case "6months":
        return utilsCommon.getDateTimeNum(
          now.subtract(6, "month").format("YYYY-MM-DD 00:00:00")
        );
      case "1year":
        return utilsCommon.getDateTimeNum(
          now.subtract(1, "year").format("YYYY-MM-DD 00:00:00")
        );
      case "2years":
        return utilsCommon.getDateTimeNum(
          now.subtract(2, "year").format("YYYY-MM-DD 00:00:00")
        );
      case "all":
        return 0;
      default:
        return utilsCommon.getDateTimeNum(now.format("YYYY-MM-DD 00:00:00"));
    }
  },
  getEndPeriod: (type: CustomDate["type"]): number => {
    const now = dayjs();
    switch (type) {
      case "1day":
        return utilsCommon.getDateTimeNum(
          now.add(1, "day").format("YYYY-MM-DD 23:59:59")
        );
      case "1week":
        return utilsCommon.getDateTimeNum(
          now.add(1, "week").format("YYYY-MM-DD 23:59:59")
        );
      case "1month":
        return utilsCommon.getDateTimeNum(
          now.add(1, "month").format("YYYY-MM-DD 23:59:59")
        );
      case "3months":
        return utilsCommon.getDateTimeNum(
          now.add(3, "month").format("YYYY-MM-DD 23:59:59")
        );
      case "6months":
        return utilsCommon.getDateTimeNum(
          now.add(6, "month").format("YYYY-MM-DD 23:59:59")
        );
      case "1year":
        return utilsCommon.getDateTimeNum(
          now.add(1, "year").format("YYYY-MM-DD 23:59:59")
        );
      case "2years":
        return utilsCommon.getDateTimeNum(
          now.add(2, "year").format("YYYY-MM-DD 23:59:59")
        );
      case "all":
        return 0;
      default:
        return utilsCommon.getDateTimeNum(now.format("YYYY-MM-DD 23:59:59"));
    }
  },
  getStartDay: (date: Date) => {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  },
  getEndDay: (date: Date) => {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    date.setMilliseconds(999);
    return date;
  },
  getDateStr: (timestamp: number): string => {
    const locale = i18n.language === "ko" ? false : true;
    var dateFormat = "YYYY-MM-DD";

    if (locale === true) {
      dateFormat = "MM/DD/YYYY";
    } else {
      dateFormat = "YYYY-MM-DD";
    }
    return dayjs(timestamp).format(dateFormat);
  },
  getDateFormat: (): string => {
    const locale = i18n.language === "ko" ? false : true;
    let dateFormat = "YYYY-MM-DD";

    if (locale === true) {
      dateFormat = "MMM DD, YYYY";
    } else {
      dateFormat = "YYYY-MM-DD";
    }
    return dateFormat;
  },
  getDateFormatCal: (): string => {
    const locale = i18n.language === "ko" ? false : true;
    let dateFormat = "yyyy-MM-dd";

    if (locale === true) {
      dateFormat = "MMM dd, yyyy";
    } else {
      dateFormat = "yyyy-MM-dd";
    }
    return dateFormat;
  },
  getValueFromKeyinEcoCommonConfig: (key: string) => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ?? "");
    else return false;

    return temp[key];
  },
  checkLicenseAvailable: (checkBy: string): boolean => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");
    else return false;
    var licenseStat = temp.ecoSystemLicenseStats;
    const ecoCommonConfig = temp.ecoCommonConfig;

    if (licenseStat) {
      var returnVal = false as boolean;

      switch (checkBy) {
        // web 미리보기 지원
        case "allowWebView":
          licenseStat.allowWebView === true
            ? (returnVal = true)
            : (returnVal = false);
          break;
        case "folderMoveOwner":
          if (ecoCommonConfig.folderMoveAllowType) {
            ecoCommonConfig.folderMoveAllowType === "MASTER"
              ? (returnVal = true)
              : (returnVal = false);
          } else {
            ecoCommonConfig.folderMoveOwner === true
              ? (returnVal = true)
              : (returnVal = false);
          }
          break;
        default:
          return false;
      }
      return returnVal;
    } else {
      return false;
    }
  },
  convertWatermark: (
    userName: string,
    userId: string,
    contentName: string,
    currentTime: string,
    currentFullTime: string,
    ip: string,
    pattern: string
  ) => {
    let loginDto = {} as any;
    const user = getUserInfoFromLocalStorage() as string | "";
    if (user) loginDto = JSON.parse(user ? user : "");
    else return false;
    const serviceConfig = loginDto.serviceConfig;

    const companyname = serviceConfig.companyName;
    const lastPos = pattern.lastIndexOf("/") + 2;
    let prefixWatermark = pattern.substring(lastPos);
    let watermarkConfig = pattern;

    let watermark;

    try {
      let watermarkPattern = watermarkConfig?.substring(0, lastPos);
      prefixWatermark = prefixWatermark === "" ? companyname : prefixWatermark;
      watermarkPattern = watermarkPattern.concat(prefixWatermark);
      watermark = watermarkPattern?.replace(
        "${username}",
        loginDto.ecoUserInfo.userType === 3 ? userId : userName
      );
      watermark = watermark.replace(
        "(${userid})",
        loginDto.ecoUserInfo.userType === 3 ? "" : `(${userId})`
      );
      watermark = watermark.replace("${contentname}", contentName);
      watermark = watermark.replace("${currenttime}", currentTime);
      watermark = watermark.replace("${currentfulltime}", currentFullTime);
      watermark = watermark.replace("${ip}", ip);
    } catch (e: any) {
      alert(e.errorMessage);
    }
    return i18n.language.includes("ar")
      ? watermark?.split("/").reverse().join("/")
      : watermark;
  },
  setKeywordToStorage: (keyword: string, userId: string): string[] => {
    // 나의 검색 키워드 저장
    let array: RecentSearch = {
      userId: userId,
      keywords: [],
    };

    let myKeyword = JSON.parse(
      "" + window.localStorage.getItem(userId + "keyword")
    );

    const trimkeyword = keyword.replace(/(^\s*)|(\s*$)/, "");
    if (myKeyword) {
      // 문자열 공백제거
      if (myKeyword.keywords.indexOf(keyword) < 0 && trimkeyword !== "") {
        if (myKeyword.keywords.length < 5) {
          myKeyword.keywords.unshift(keyword);
        } else {
          myKeyword.keywords.unshift(keyword);
          myKeyword.keywords.pop();
        }
      }

      // 최근검색어 리스트에 담긴 검색어를 다시 검색했을 때,
      if (myKeyword.keywords.indexOf(keyword) > 0) {
        const index = myKeyword.keywords.indexOf(keyword);
        myKeyword.keywords.splice(index, 1);
        myKeyword.keywords.unshift(keyword);
      }

      myKeyword.userId = userId;
      window.localStorage.setItem(
        userId + "keyword",
        JSON.stringify(myKeyword)
      );
    } else {
      if (trimkeyword && trimkeyword !== "") {
        array.keywords.push(keyword);
      }
      window.localStorage.setItem(userId + "keyword", JSON.stringify(array));
    }
    return array.keywords;
  },
  getKeywordToStorage: (userId: string): string[] => {
    // 나의 최신 검색어 가져오기
    const myKeyword: RecentSearch = JSON.parse(
      "" + window.localStorage.getItem(userId + "keyword")
    );

    return myKeyword && myKeyword.keywords ? myKeyword.keywords : [];
  },
  deleteKeywordToStorage: (keyword: string, userId: string): string[] => {
    // 나의 최신 검색어 하나 삭제
    const myKeyword: RecentSearch = JSON.parse(
      "" + window.localStorage.getItem(userId + "keyword")
    );

    if (myKeyword) {
      if (myKeyword.keywords.indexOf(keyword) > -1) {
        myKeyword.keywords.splice(myKeyword.keywords.indexOf(keyword), 1);
        if (myKeyword.keywords.length === 0) {
          window.localStorage.removeItem(userId + "keyword");
        } else {
          window.localStorage.setItem(
            userId + "keyword",
            JSON.stringify(myKeyword)
          );
        }
      }

      return myKeyword.keywords;
    } else {
      return [];
    }
  },
  setCurrentKeywordToStorage: (keyword: string): string => {
    window.localStorage.setItem("currentKeyword", keyword);
    return keyword;
  },
  checkExtensionAvailable: (docName: string, checkBy: string): boolean => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");
    else return false;
    var serviceConfig = temp.serviceConfig;
    var fileType = docName
      ? docName.toLowerCase().substr(docName.lastIndexOf(".") + 1)
      : "";
    var returnVal = false as boolean;
    switch (checkBy) {
      case "disableExtensions":
        serviceConfig.disableExtensions?.includes(fileType)
          ? (returnVal = true)
          : (returnVal = false);
        break;
      case "fwocExtensions":
        serviceConfig.fwocExtensions?.includes(fileType)
          ? (returnVal = true)
          : (returnVal = false);
        break;

      /* manual Revision Extension에 추가 된 파일들은 수동리비전이 가능합니다. */
      case "manualRevisionExtensions":
        serviceConfig.manualRevisionExtensions?.includes(fileType)
          ? (returnVal = true)
          : (returnVal = false);
        break;
      case "revisionExtensions":
        serviceConfig.revisionExtensions?.includes(fileType)
          ? (returnVal = true)
          : (returnVal = false);
        break;
      case "webRevisionExtensions":
        serviceConfig.webRevisionExtensions?.includes(fileType)
          ? (returnVal = true)
          : (returnVal = false);
        break;
      case "hylandExtensions":
        serviceConfig.hylandExtensions?.includes(fileType.toLowerCase())
          ? (returnVal = true)
          : (returnVal = false);
        break;
      case "aiChatExtensions":
        ".docx.doc.pptx.ppt.xlsx.xls.hwp.hwpx.pdf.txt"?.includes(
          fileType.toLowerCase()
        )
          ? (returnVal = true)
          : (returnVal = false);
        break;
      default:
        return false;
    }
    return returnVal;
  },
  // 워크그룹 생성 가능한 사용자
  // 1. workgroupCreatePermission 이 ADMIN일 경우 -> 사용자가 admin 계정인지 확인
  // 2. workgroupCreatePermission 이 INTERNAL일 경우 -> 사용자가 내부 사용자인지 확인
  // 3. workgroupCreatePermission 이 EXERNAL일 경우 -> 항상 통과
  checkWorkgroupCreatePermission: (loginDto?: LoginDto): boolean => {
    if (loginDto) {
      const ecoUserInfo = loginDto.ecoUserInfo;
      const ecoCommonConfig = loginDto.ecoCommonConfig;
      if (
        ecoCommonConfig.workgroupCreatePermission === "ADMIN" &&
        ecoUserInfo.admin
      ) {
        return true;
      } else if (
        ecoCommonConfig.workgroupCreatePermission === "INTERNAL" &&
        ecoUserInfo.userType === 1
      ) {
        return true;
      } else if (ecoCommonConfig.workgroupCreatePermission === "EXTERNAL") {
        return true;
      }
      return false;
    }
    return false;
  },
  // 좌측 사이드 메뉴에 워크그룹 리스트 표현 방식
  // 1. workgroupTreeViewPermission 이 ALL일 경우 -> 항상 true
  // 2. workgroupTreeViewPermission 이 NONE일 경우 -> 항상 false
  // 3. workgroupTreeViewPermission 이 INTERNAL_USER일 경우 -> 내부 사용자인지 확인

  checkSideMenuShowNestedType: (loginDto?: LoginDto): boolean => {
    if (
      loginDto?.ecoCommonConfig.workgroupTreeViewPermission === "INTERNAL_USER"
    )
      return loginDto?.ecoUserInfo.userType === 1;
    else
      return loginDto?.ecoCommonConfig?.workgroupTreeViewPermission === "ALL";
  },
  // 문서 업로드 가능한 사용자인지 체크
  // 1. fileUploadPermission 이 ADMIN일 경우 -> 사용자가 admin 계정인지 확인
  // 2. fileUploadPermission 이 INTERNAL일 경우 -> 사용자가 내부 사용자인지 확인
  // 3. fileUploadPermission 이 EXERNAL일 경우 -> 항상 통과
  checkUploadFilePermission: (loginDto?: LoginDto): boolean => {
    if (loginDto) {
      const ecoUserInfo = loginDto.ecoUserInfo;
      const ecoCommonConfig = loginDto.ecoCommonConfig;
      if (
        ecoCommonConfig.fileUploadPermission === "ADMIN" &&
        ecoUserInfo.admin
      ) {
        return true;
      } else if (
        ecoCommonConfig.fileUploadPermission === "INTERNAL" &&
        ecoUserInfo.userType === 1
      ) {
        return true;
      } else if (ecoCommonConfig.fileUploadPermission === "EXTERNAL") {
        return true;
      }
      return false;
    }
    return false;
  }, // 워크그룹장의 랩핑 해제 권한 체크
  // autoUnwrappingPermission이 true인 경우, 사용자가 워크그룹자인지 체크
  // autoUnwrappingPermission이 false인 경우, 사용자가 문서 업로더인지 체크
  checkAutoUnwrapPermission: (
    loginDto: LoginDto,
    isWorkgroupMaster: boolean,
    isFileUploader: boolean
  ): boolean => {
    if (loginDto) {
      const ecoCommonConfig = loginDto.ecoCommonConfig;
      // 실제로 정책에 암호화 해제 다운로드 여부 판단
      if (ecoCommonConfig.unwrappingPermission === "NONE") return false;
      if (ecoCommonConfig.autoUnwrappingPermission && isWorkgroupMaster) {
        return true;
      } else if (isFileUploader) return true;
    }
    return false;
  },
  getWorkspaceCode: (): string | null => {
    return window.sessionStorage.getItem("fasoo-workspace-code");
  },
  isDocInfo(obj: any): obj is DocInfo {
    return obj && typeof obj === "object" && "syncId" in obj && !!obj.syncId;
  },
  isMeetingInfo(obj: any): obj is meetingDTO {
    return obj && typeof obj === "object" && "meetingId" in obj;
  },
  isWorkgroupId(obj: any): obj is Workgroup {
    return obj && typeof obj === "object" && "workgroupId" in obj;
  },
  isFolderInfo(obj: any): obj is FolderInfoDTO {
    return obj && typeof obj === "object" && "folderId" in obj;
  },
  checkVisibleOptions: (
    type: string,
    page?: string,
    isWorkgroupManager?: boolean
  ) => {
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);
    switch (type) {
      case "webview":
        return (
          window.eco.serviceType !== "ONPREMISE" ||
          utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED"
        );
      case "mobile":
        return (
          window.eco.serviceType !== "ONPREMISE" &&
          utilsCommon.checkSupportPolicy("Mobile") === "SUPPORT_ACCEPTED"
        );
      case "mac":
        return (
          window.eco.serviceType !== "ONPREMISE" &&
          utilsCommon.checkSupportPolicy("Mac") === "SUPPORT_ACCEPTED"
        );
      case "advandedPolicy":
      case "readRestrict":
        return !(
          window.eco.serviceType === "OCI" &&
          utilsCommon.getWorkspaceCode() === "MTECOCOMMON00001"
        );
      /* openSetting: 내부 사용자 열람 */
      case "openSetting":
        return (
          window.eco.serviceType !== "OCI" &&
          page !== "SEND_FILE" &&
          isWorkgroupManager
        );
      /* 파일 전송하기*/
      case "sendFile":
        return (
          loginDto?.ecoUserInfo?.userType === 1 &&
          utilsCommon.checkUploadFilePermission(loginDto)
        );
    }
    return true;
  },
  sleep: (ms: number): Promise<any> => {
    return new Promise((r) => setTimeout(r, ms));
  },
  getAIChatAvailibility: (): boolean => {
    var temp = {} as any;
    var ecoUser = window.localStorage.getItem("fasoo-eco-user");
    var aiViewType =
      window.eco && window.eco.aiViewType !== ""
        ? window.eco.aiViewType
        : "none";
    if (ecoUser) temp = JSON.parse(ecoUser ? ecoUser : "");
    else return false;

    if (aiViewType === "none") {
      return false;
    }

    var serviceConfig = temp.serviceConfig;
    if (serviceConfig.aiDocumentChatAvailibility) {
      if (aiViewType === "internal" && temp.ecoUserInfo.userType === 2) {
        return false;
      } else if (aiViewType === "external" && temp.ecoUserInfo.userType === 1) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  },
  checkMenuAvailble: (
    type: string,
    item?: DocInfo | meetingDTO | Workgroup | FolderInfoDTO,
    page?: string
  ) => {
    if (!item) return false;
    const user = getUserInfoFromLocalStorage() as string | "";
    const loginDto: LoginDto = JSON.parse(user);
    const myId = loginDto?.userInfo?.userId ?? "";
    const isDeleted =
      utilsCommon.isDocInfo(item) &&
      (page === "trash" || page?.includes("manageStorage"));
    switch (type) {
      /** 1. preview: 열기 */
      case "preview":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          utilsCommon.checkLicenseAvailable("allowWebView") &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "fwocExtensions"
          )
        );
      /** editMode: 파일 편집 */
      case "editMode":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "fwocExtensions"
          ) &&
          window.eco.isCspp &&
          utilsCommon.checkExtensionAvailable(
            item?.contentName,
            "webRevisionExtensions"
          )
        );
      /**
       * 2. shareLink: 링크 공유
       * 3. download: 다운로드
       * 4. moveFile: 파일 위치로 이동
       * */
      case "shareLink":
      case "download":
      case "moveFile":
        return utilsCommon.isDocInfo(item) && !isDeleted;
      /**
       * 4. auth: 파일 사용 권한
       * 5. requestOpen: 열람요청
       */
      case "requestOpen":
      case "auth":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          loginDto?.ecoUserInfo?.userType !== 3 &&
          page !== "receivedFile" &&
          !(
            item.workgroup.workgroupId === "NONEWORKGROUP" &&
            myId !== (item.master ?? item.masterUser)?.userId?.toLowerCase()
          )
        );
      case "leaveComment":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          loginDto?.ecoUserInfo?.userType !== 3 &&
          page !== "receivedFile"
        );
      /**
       * shareWorkgroup: 워크그룹으로 공유
       */
      case "shareWorkgroup":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" &&
          myId === (item.master ?? item.masterUser)?.userId?.toLowerCase()
        );
      /**
       * restore: 복원
       * deletePerm: 영구 삭제
       * */
      case "restore":
      case "deletePerm":
        return utilsCommon.isDocInfo(item) && isDeleted && page === "trash";
      /**
       * adminTrash: 저장 공간 사용 현황에서 삭제
       * */
      case "adminTrash":
        return utilsCommon.isDocInfo(item) && page?.includes("manageStorage");
      /** favorite: 즐겨찾기 해제
       */
      case "favorite":
        return (
          utilsCommon.isDocInfo(item) && !isDeleted && item?.favoritDate > 0
        );
      /** notFavorite: 즐겨찾기 추가
       */
      case "notFavorite":
        return (
          utilsCommon.isDocInfo(item) && !isDeleted && item.favoritDate === 0
        );
      /** checkout: 수동 리비전 시작 */
      case "checkout":
        return (
          !isDeleted &&
          utilsCommon.isDocInfo(item) &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "manualRevisionExtensions"
          ) &&
          page === "workgroupFile" &&
          utilsCommon.checkUploadFilePermission(loginDto) &&
          item.authType === "R" &&
          item.checkoutUser?.userId === ""
        );
      /**
       * linkWorkgroup:
       * changeName: 파일 이름 변경
       * delete: 삭제
       */
      case "linkWorkgroup":
      case "changeName":
      case "delete":
        return (
          page !== "client_preview" &&
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          myId === (item.master ?? item.masterUser)?.userId?.toLowerCase()
        );
      case "divider":
        return (
          page !== "client_preview" &&
          ((utilsCommon.isDocInfo(item) &&
            !isDeleted &&
            myId === (item.master ?? item.masterUser)?.userId?.toLowerCase()) ||
            utilsCommon.isFolderInfo(item))
        );
      /** changeMaster: 파일 관리자 변경
       */
      case "changeMaster":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          page === "workgroupFile" &&
          myId === (item.master ?? item.masterUser)?.userId?.toLowerCase() &&
          item.workgroup.workgroupId !== "NONEWORKGROUP"
        );
      /** aiChat: AI 채팅
       */
      case "aiChat":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          utilsCommon.getAIChatAvailibility() &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "aiChatExtensions"
          ) &&
          item.myPolicy?.policy?.webView &&
          item.myPolicy?.policy?.download &&
          item?.myPolicy?.uniqueId !== "ONLYVIEW" &&
          loginDto?.ecoUserInfo?.userType !== 3
        );
      /**
       * checkin: 수동 리비전 완료
       * undoCheckout: 수동 리비전 취소
       * */
      case "checkin":
      case "undoCheckout":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "manualRevisionExtensions"
          ) &&
          utilsCommon.checkUploadFilePermission(loginDto) &&
          item.authType === "R" &&
          item.checkoutUser?.userId?.toLowerCase() === myId
        );
      /** forceCheckin: 강제 리비전 완료 */
      case "forceCheckin":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          item.concurrentUserId !== null &&
          !!item.concurrentUserId &&
          (item.master ?? item.masterUser)?.userId?.toLowerCase() === myId
        );
      /** unAuthDownload: 미인증 다운로드 */
      case "unAuthDownload":
        return utilsCommon.isDocInfo(item) &&
          item.authType === "N" &&
          myId === item.workgroup.managerId?.toLowerCase()
          ? true
          : page === "client"
          ? utilsCommon.isDocInfo(item) &&
            !isDeleted &&
            loginDto &&
            utilsCommon?.checkAutoUnwrapPermission(
              loginDto,
              myId === item.workgroup?.managerId?.toLowerCase(),
              false
            ) &&
            item?.authType === "N" &&
            !(
              window.eco.serviceType === "OCI" &&
              utilsCommon.getWorkspaceCode() === "MTECOCOMMON00001"
            )
          : page !== "client_preview" &&
            utilsCommon.isDocInfo(item) &&
            !isDeleted &&
            loginDto &&
            utilsCommon?.checkAutoUnwrapPermission(
              loginDto,
              myId === item.workgroup?.managerId?.toLowerCase(),
              myId === (item.master ?? item.masterUser)?.userId.toLowerCase()
            ) &&
            !(
              window.eco.serviceType === "OCI" &&
              utilsCommon.getWorkspaceCode() === "MTECOCOMMON00001"
            );
      /** folderMove: 다른 폴더로 이동 */
      case "folderMove":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          page === "workgroupFile" &&
          item.workgroup.workgroupId !== "NONEWORKGROUP" &&
          (myId === (item.master ?? item.masterUser)?.userId?.toLowerCase() ||
            !utilsCommon.checkLicenseAvailable("folderMoveOwner"))
        );
      /*  googleUpload: Google Drive문서 올리기
       */
      case "googleUpload":
        return (
          utilsCommon.isDocInfo(item) &&
          !isDeleted &&
          utilsCommon.checkUploadFilePermission(loginDto) &&
          window.eco.companyName === "workspace"
        );
      /**
       * startMeeting: 회의 바로 시작
       * meetingDelete: 삭제
       * linkMeeting: 회의 초대 링크
       */
      case "startMeeting":
      case "deleteMeeting":
        return (
          utilsCommon.isMeetingInfo(item) &&
          item.participantListStr?.split(",")?.includes(myId) &&
          item.ownerId === myId &&
          item.endTime > dayjs().valueOf()
        );
      /**
       * linkMeeting: 회의 초대 링크
       */
      case "linkMeeting":
        return (
          utilsCommon.isMeetingInfo(item) &&
          item.participantListStr?.split(",")?.includes(myId) &&
          item.ownerId === myId &&
          item.endTime > dayjs().valueOf() &&
          page !== "home"
        );
      /** meetingDetail: 회의 상세보기
       * copyLinkMeeting: 링크 복사
       */
      case "meetingDetail":
      case "copyLinkMeeting":
        return (
          utilsCommon.isMeetingInfo(item) &&
          item.participantListStr?.split(",")?.includes(myId) &&
          item.ownerId === myId &&
          item.endTime > dayjs().valueOf() &&
          page === "home"
        );
      /**
       * editMeeting: 회의 수정
       */
      case "editMeeting":
        return (
          utilsCommon.isMeetingInfo(item) &&
          item.ownerId === myId &&
          item.endTime > dayjs().valueOf() &&
          window.location.href.includes("/workgroup/")
        );
      /**
       * enterMeeting: 회의 참석
       */
      case "enterMeeting":
        return (
          utilsCommon.isMeetingInfo(item) &&
          item.participantListStr?.split(",")?.includes(myId) &&
          item.ownerId !== myId &&
          item.endTime > dayjs().valueOf()
        );
      /** webRevision: 웹 리비젼 */
      case "webRevision":
        return (
          utilsCommon.isDocInfo(item) &&
          item.status === "N" &&
          item.authType === "R" &&
          item.checkoutUser?.userId === "" &&
          utilsCommon.checkExtensionAvailable(
            item.contentName,
            "webRevisionExtensions"
          )
        );
      /** workgroupFav: 워크그룹 고정 */
      case "workgroupFav":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item?.favorite !== "N" &&
          (item.endDate === -1 || item.endDate > dayjs().valueOf())
        );
      /** workgroupRename: 워크그룹 이름 변경 */
      case "workgroupRename":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item.managerId === myId &&
          (item.endDate === -1 || item.endDate > dayjs().valueOf())
        );
      /** workgroupFav: 워크그룹 고정 해제*/
      case "workgroupUnFav":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item?.favorite === "N" &&
          (item.endDate === -1 || item.endDate > dayjs().valueOf())
        );
      /**
       * workgroupInfo_user:  워크그룹 상세정보 사용자
       * divider_workgroup: 워크그룹 나가기 사단
       *   exitWorkgroup:  워크그룹 나가기
       */
      case "workgroupInfo_user":
      case "divider_workgroup":
      case "exitWorkgroup":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item?.managerId?.toLowerCase() !== myId
        );
      /**
       * workgroupInfo_master:  워크그룹 상세정보 워크그룹장
       * renameWorkgroup: 워크그룹 이름 변경
       */
      case "renameWorkgroup":
      case "workgroupInfo_master":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item?.managerId?.toLowerCase() === myId
        );
      /**
       * createSubWorkgroup: 하위 워크그룹 생성
       */
      case "createSubWorkgroup":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item.workgroupType === "GENERAL" &&
          utilsCommon.checkWorkgroupCreatePermission(loginDto) &&
          utilsCommon.checkSideMenuShowNestedType(loginDto) &&
          ((loginDto.ecoCommonConfig.workgroupTreeCreatePermission ===
            "MASTER" &&
            loginDto.userInfo.userId === item.managerId) ||
            (loginDto.ecoCommonConfig.workgroupTreeCreatePermission ===
              "INTERNAL_USER" &&
              loginDto.ecoUserInfo.userType === 1))
        );
      /**
       * moveWorkgroup: 워크그룹 위치 이동
       */
      case "moveWorkgroup":
        return (
          utilsCommon.isWorkgroupId(item) &&
          item.workgroupType !== "PRIVATE" &&
          utilsCommon.checkSideMenuShowNestedType(loginDto) &&
          item.managerId === loginDto.userInfo.userId
        );
      /**
       * changeFolderName: 폴더 이름 변경
       * folderdelete: 폴더 삭제
       */
      case "changeFolderName":
      case "folderdelete":
        return utilsCommon.isFolderInfo(item);
      /** inviteUser: 멤버 추가 */
      case "inviteUser":
        return (
          utilsCommon.isWorkgroupId(item) &&
          (item.invitePolicy === 1 ||
            (item.invitePolicy === 2 && loginDto.ecoUserInfo.userType !== 2) ||
            (item.invitePolicy === 0 &&
              item?.managerId?.toLowerCase() === myId)) &&
          !window.location.href.includes("/manageWorkgroup")
        );
      /** shareDocToChild: 파일 공유 */
      case "shareDocToChild":
        return utilsCommon.isWorkgroupId(item) && item.workgroupType === "HUB";
    }
  },
  getUserColor: (
    userType?: number | null,
    userStaus?: number,
    userName?: string | null
  ) => {
    return (userStaus && userStaus === 2) ||
      (userName && userName === null) ||
      userName?.trim() === ""
      ? "%%NOUSER%%"
      : userType === 1
      ? "%%INTERNALUSER%%"
      : userType === 2
      ? "%%EXTERNALUSER%%"
      : "%%NOUSER%%";
  },
  getUserNameIdTag: (
    userId: string,
    userName?: string | null,
    withOutUserId?: boolean,
    userType?: number
  ) => {
    return userName === "#NONE_USER_NAME#"
      ? t("common.unAuthUser")
      : userType === 3
      ? userId
      : !!userName && withOutUserId
      ? userName
      : !!userName
      ? `${userName} (${userId})`
      : `${userId}`;
  },
  getAuthColor: (auth: string) => {
    switch (auth) {
      case "MASTER":
        return "#05C072";
      case "WORKGROUP":
        return "#3182F6";
      case "PERSONAL":
        return "#FAA131";
      case "NONE":
        return "#7287A2";
    }
  },
  renderWrapsodyECOLogo: () => {
    return ["http://localhost:3000", "http://127.0.0.1:3000"].includes(
      window.origin
    )
      ? "https://fasoo.wrapsodyeco.com/eco-gateway/resources/images/main_bi_img.png"
      : window.origin + "/eco-gateway/resources/images/main_bi_img.png";
  },
  getUserPositionNDept: (req: {
    positionName?: string | null;
    deptName?: string | null;
    companyName?: string | null;
    userType?: number;
  }) => {
    return req.userType !== 1 || window.eco.serviceType === "OCI"
      ? req.deptName
      : [req.companyName, req.deptName, req.positionName]
          .filter((item, index) => !!item && index < 2)
          .join(" / ");
  },
  getMyId: (): string => {
    let userLocalInfo = window.localStorage.getItem("fasoo-eco-user") ?? "";
    let indexUserId = userLocalInfo.indexOf("userId") + 9;
    return userLocalInfo
      .substring(indexUserId, userLocalInfo.indexOf('"', indexUserId))
      .toLowerCase();
  },
  fileNameValidation: (fileName: string): boolean => {
    const regex = /[\\/:*?"<>|]/;
    return regex.test(fileName);
  },
  convertRightMenu: (right: string) => {
    let returnValue = "";
    switch (right) {
      case "eco_organ":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "조직 관리";
        } else {
          returnValue = "Manage Organization";
        }

        break;
      case "eco_position_role":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "직급/직무 설정";
        } else {
          returnValue = "Job Title/Role";
        }

        break;
      case "eco_manage_admin":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "관리자 설정";
        } else {
          returnValue = "Administrator Setting";
        }

        break;
      case "eco_workgroup":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "워크그룹 관리";
        } else {
          returnValue = "Manage Workgroup";
        }
        break;
      case "eco_common_policy":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "전사 정책";
        } else {
          returnValue = "Policy";
        }
        break;
      case "eco_doc_usage":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "파일 사용 로그";
        } else {
          returnValue = "File Usage Log";
        }
        break;
      case "eco_storage":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "스토리지 관리";
        } else {
          returnValue = "Manage Storage";
        }
        break;
      case "eco_workgroup_log":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "워크그룹 이력";
        } else {
          returnValue = "Workgroup Log";
        }
        break;
      case "eco_login_log":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "로그인 이력";
        } else {
          returnValue = "Login Log";
        }
        break;

      case "eco_trash":
        if (utilsCommon.getLanguage() === "ko") {
          returnValue = "휴지통";
        } else {
          returnValue = "Trash";
        }
        break;
    }

    return returnValue;
  },
  getNameWithId: (name: string, id: string, userType?: number): string => {
    if (name) {
      return id === "#NONE_USER_ID#"
        ? utilsCommon.getLanguage() === "ko"
          ? "미인증 사용자"
          : "Unauthorized user"
        : userType === 3
        ? name
        : name + "(" + id + ")";
    } else {
      return id;
    }
  },

  ipNumber: (ip: string) => {
    const regNum = /^[.]?[0-9.]*$/;
    return regNum.test(ip);
  },
  checkIpRegex: (ip: string) => {
    const regNum =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return regNum.test(ip);
  },
  checkCdn: (isAdmin: boolean) => {
    const loginResult = isAdmin
      ? getAdminInfoFromLocalStorage()
      : getUserInfoFromLocalStorage();
    const serviceConfig =
      loginResult !== null && JSON.parse(loginResult).serviceConfig;
    return serviceConfig.cdnAvailibility ? true : false;
  },
  getServiceConfig: (isAdmin: boolean, type: string) => {
    const loginResult =
      isAdmin && window.eco.serviceType !== "OCI"
        ? getAdminInfoFromLocalStorage()
        : getUserInfoFromLocalStorage();
    const serviceConfig =
      loginResult !== null && JSON.parse(loginResult)?.serviceConfig;
    return serviceConfig[type];
  },
  isAvailableWebOffice: (types: any, value: string) => {
    let returnValue = false;

    switch (value) {
      case "OOS":
        if (types.oosUse) {
          returnValue = true;
        }
        break;
      case "OFFICE365":
        if (types.office365Use) {
          returnValue = true;
        }
        break;
      case "THINKFREE":
        if (types.tfsiUse) {
          returnValue = true;
        }
        break;
      case "COLLABORA":
        if (types.collaboraUse) {
          returnValue = true;
        }
        break;
      default:
        break;
    }
    return returnValue;
  },
  isAvailableAnotherWebOffice: (types: any) => {
    for (var key in types) {
      if (types[key] === true) {
        return true;
      }
    }

    return false;
  },
  isClientLogin: (type: "MAC" | "WINDOWS") => {
    const agent = navigator.userAgent.toLowerCase();
    if (type === "MAC") {
      return (
        agent.includes("macintosh") &&
        !agent.includes("safari") &&
        (window as any)?.webkit?.messageHandlers
      );
    } else {
      return window?.chrome?.webview && window?.chrome?.webview?.hostObjects;
    }
  },
  validationCompanyName: (companyName: string) => {
    const regex = /^([a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ!@#$%()[\]_-]{0,30})$/;
    return regex.test(companyName);
  },

  validationIpBand: (ipFromStr: string, ipToStr: string) => {
    const ipRegx = (number: string) => {
      const regNum =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      return regNum.test(number);
    };

    const calcIpAddress = (array: number[]): number => {
      return array.reduce(
        (accum: number, currVal: string | number, index: number) => {
          let calcValue = 0;
          const setNumber = Number(currVal);
          switch (index) {
            case 1:
              calcValue = setNumber * 16777216;
              break;
            case 2:
              calcValue = setNumber * 65536;
              break;
            case 3:
              calcValue = setNumber * 256;
              break;
            case 4:
              calcValue = setNumber * 1;
              break;
          }

          return Number(accum + calcValue);
        }
      );
    };

    if (ipFromStr === "" && ipToStr === "") {
      return false;
    } else if (
      (ipFromStr !== "" && ipToStr === "") ||
      (ipFromStr === "" && ipToStr !== "")
    ) {
      return false;
    } else if (ipRegx(ipFromStr) === false || ipRegx(ipToStr) === false) {
      return false;
    } else {
      const from = calcIpAddress(ipFromStr.split(".").map(Number));
      const to = calcIpAddress(ipToStr.split(".").map(Number));
      return from <= to;
    }
  },
  changeStatusCodeToStringForInfo: (userType: number, status: number) => {
    let value = "";
    if (userType === 3)
      if (utilsCommon.getLanguage() === "ko") {
        return "OTP 인증";
      } else {
        return "OTP verified";
      }
    switch (status) {
      case 1:
        if (utilsCommon.getLanguage() === "ko") {
          value = "가입 완료";
        } else {
          value = "Joined";
        }

        break;
      case 2:
        if (utilsCommon.getLanguage() === "ko") {
          value = "가입 대기";
        } else {
          value = "Invited";
        }

        break;
      case 3:
        if (utilsCommon.getLanguage() === "ko") {
          value = "가입 완료";
        } else {
          value = "Joined";
        }
        break;
      case 4:
        if (utilsCommon.getLanguage() === "ko") {
          value = "탈퇴";
        } else {
          value = "Left project";
        }

        break;
      default:
        break;
    }

    return value;
  },
  renderProperWorkgroupName: (workgroupId?: string, workgroupName?: string) => {
    return workgroupId === "NONEWORKGROUP" ? "My Box" : workgroupName;
  },
  renderProperNavigatePath: (path: string) => {
    return window.eco.serviceType === "OCI" && getWorkspaceName()
      ? `${process.env.PUBLIC_URL}/${getWorkspaceName()}${path}`
      : window.eco.serviceType === "OCI"
      ? `${process.env.PUBLIC_URL}/My Space${path}`
      : `${process.env.PUBLIC_URL}${path}`;
  },
  isDemoEco: () => {
    return (
      window.location.hostname === DEMO_HOSTNAME ||
      window.eco.serviceType === "DEMO"
    );
  },
};
export default utilsCommon;
